import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { CheckPicker, DateRangePicker, SelectPicker } from 'rsuite';
import { endOfDay, isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { timeZonesList } from '../profileSettings/ProfileSettingsPreferences';
import { RenderExtraFooter } from '../filter/RenderExtraFooter';
import { getFormattedDate, makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { FilterComponent } from '../FilterComponent';
import { useOrdersContext } from '../../store/order/order-context';
import clsx from 'clsx';
import { NumberFilterComponent } from '../filter/TextFilterComponent';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { OrdersFiltersSideModal } from './OrdersFiltersSideModal';
import {
	cleanOrderFilters,
	getOrdersListQueryParamsV2,
	IOrderFilters,
	savedOrderFilters, setSavedFavoriteOrderFilters,
} from '../../store/order/actions/order';
import {
	getFormattedFavoriteOrderFilters,
	getFormattedOrderFiltersFormFields,
	getInitialOrderFiltersValues, getOrdersFiltersDetailsSchema,
	ORDERS_FILTERS_FORM_FIELDS,
	ORDERS_FILTERS_FORM_FIELDS_NAMES,
} from '../../constants/order/order-filters-form';
import { getFavoriteFiltersFromStorage } from '../../utils/filter-helpers';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { toAbsoluteUrl } from '../../utils';
import { getTableSort, getTableSortField } from '../../utils/linksHelper';
import { useFormik } from 'formik';

const typesList = [
	{
		label: 'MIT',
		value: 'MIT'
	},
	{
		label: 'CIT',
		value: 'CIT'
	},
];

interface Props {
	setRefreshFilters: (value: boolean) => void;
}

export const OrderFilterSection: FC<Props> = ({ setRefreshFilters }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();

	const picker = useRef(null);
	const siteIdRef = useRef(null);
	const partnerIdRef = useRef(null);
	const orderIdRef = useRef(null);
	const externalOrderIdRef = useRef(null);
	const emailRef = useRef(null);
	const firstNameRef = useRef(null);
	const lastNameRef = useRef(null);
	const binRef = useRef(null);
	const last4Ref = useRef(null);

	const { orderContextState, dispatch } = useOrdersContext();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const {
		orderStatuses,
		isOrderDataLoading,
		status,
		orderId,
		email,
		firstName,
		lastName,
		pid,
		bin,
		last4,
		pageSize,
		timezone,
		sid,
		externalOrderId,
		isMIT,
		placedFrom,
		placedTo,
		from,
		to,
		query,
		orderSorting
	} = orderContextState;
	const { merchantState: { merchantData, chosenMerchant }} = useMerchants();

	const appliedFilters: IOrderFilters = parseSearchParams(search);
	const savedOrderFavoriteFilters: string[] = getFavoriteFiltersFromStorage('orders');

	const [searchValue, setSearchValue] = useState<string>(appliedFilters?.query ?? query);
	const [isRefreshed, setIsRefreshed] = useState<boolean>(true);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const formik = useFormik({
		initialValues: getInitialOrderFiltersValues(orderStatuses, checkRoles(authUser, CLAIMS.MERCHANT_ADMIN)),
		validationSchema: getOrdersFiltersDetailsSchema(t),
		onSubmit: async () => {
			if (!formik.isValid) return;

			const formattedData = getFormattedOrderFiltersFormFields(formik.values);
			const formattedFavoriteFilters = getFormattedFavoriteOrderFilters(formik.values);

			setSavedFavoriteOrderFilters(dispatch, formattedFavoriteFilters);

			if (chosenMerchant) {
				formattedData.mid = chosenMerchant;
			}

			formattedData.pageNumber = 0;
			formattedData.pageSize = pageSize;
			formattedData.sort = getTableSort(orderSorting);
			formattedData.sortField = getTableSortField(orderSorting);

			const params = getOrdersListQueryParamsV2({
				state: orderContextState,
				filterData: formattedData
			});

			savedOrderFilters(dispatch, { ...params });

			const queryString = makeSearchParams(params);

			const decodedQueryString = decodeURIComponent(queryString);

			navigate(`${pathname}?${decodedQueryString}`);

			setIsRefreshed(true);

			handleFilterModalClose();
		}
	});

	const isSelectedFilters = status?.length
		|| placedFrom
		|| placedTo
		|| from
		|| to
		|| isMIT
		|| sid
		|| pid
		|| orderId
		|| externalOrderId
		|| email
		|| firstName
		|| lastName
		|| bin
		|| last4
		|| timezone !== 'UTC'
		|| query;

	const handleCleanFilter = (): void => {
		cleanOrderFilters(dispatch, orderStatuses);
		setSearchValue('');
		setIsRefreshed(false);
	};

	useEffect(() => {
		savedOrderFilters(dispatch, appliedFilters, orderStatuses);
	}, [orderStatuses, search]);

	useEffect(() => {
		return (): void => {
			handleCleanFilter();
		};
	}, []);

	const allValue = orderStatuses?.map(item => item.value);

	const handleCheckAll = (value, checked): void => {
		const statuses = checked ? allValue : [];
		savedOrderFilters(dispatch, { status: statuses }, orderStatuses);
		setIsRefreshed(false);
	};

	const onCloseModal = ():void => setModalState(prevState => ({ ...prevState, isOpen: false }));

	const handleFilterModalClose = ():void => setIsFilterModalOpen(false);

	const todayStart = new Date();
	const todayEnd = new Date();
	todayStart.setHours(0, 0, 0);
	todayEnd.setHours(23, 59, 59);
	const showStartDate = getFormattedDate(todayStart, 'yyyy-MM-dd HH:mm:ss');
	const showEndDate = getFormattedDate(todayEnd, 'yyyy-MM-dd HH:mm:ss');


	const handleSearchChange = (value: string): void => {
		setSearchValue(value);
		savedOrderFilters(dispatch, { query: value });
	};

	const handleSearch = async (): Promise<void> => {
		setRefreshFilters(true);
		const orderFilters: IOrderFilters = {
			status,
			timezone,
			sid,
			pid,
			externalOrderId,
			query,
			placedFrom,
			placedTo,
			from,
			to,
			isMIT,
			orderId,
			email,
			firstName,
			lastName,
			bin,
			last4,
			pageSize: pageSize || 10,
			pageNumber: 0,
			sort: getTableSort(orderSorting),
			sortField: getTableSortField(orderSorting),
			mid: chosenMerchant,
		};
		const formattedInitialData = getOrdersListQueryParamsV2({
			state: orderContextState,
			filterData: orderFilters
		});
		console.log('formattedInitialData', formattedInitialData);
		navigate(`${pathname}?${makeSearchParams(formattedInitialData)}`);
		setIsRefreshed(true);
	};

	return (
		<div className="card bg-transparent">
			<div className="card-body py-0 px-0">
				<div className="d-sm-flex align-items-center justify-content-between mb-5">
					<div className="d-flex align-items-center block-width application-selector">
						<SearchInputSmall
							placeholder={t('ORDERS.SEARCH_PLACEHOLDER')}
							searchValue={searchValue}
							setSearchValue={handleSearchChange}
							setIsRefreshed={setIsRefreshed}
						/>
						<ButtonComponent
							additionalClass="btn-sm ms-10 w-100px d-none d-sm-block"
							onButtonClick={(): Promise<void> => !isRefreshed && handleSearch()}
							isDisabled={isOrderDataLoading || isRefreshed}
							loading={isOrderDataLoading}
						>
							<>
								{
									isRefreshed
										? 'Search'
										: (
											<div className="d-flex align-items-center justify-content-center">
												<FontAwesomeIcon icon={faTriangleExclamation} />
												<span className="ms-2 fw-bolder">Refresh</span>
											</div>
										)
								}
							</>
						</ButtonComponent>

					</div>
					<ButtonComponent
						additionalClass="btn-sm d-block d-sm-none mt-5 w-100"
						onButtonClick={(): Promise<void> => !isRefreshed && handleSearch()}
						isDisabled={isOrderDataLoading || isRefreshed}
						loading={isOrderDataLoading}
					>
						<>
							{
								isRefreshed
									? 'Search'
									: (
										<div className="d-flex align-items-center justify-content-center">
											<FontAwesomeIcon icon={faTriangleExclamation} />
											<span className="ms-2 fw-bolder">Refresh</span>
										</div>
									)
							}
						</>
					</ButtonComponent>
				</div>
				<div className="d-flex align-items-center fc-filter-line">
					<FilterComponent value={status?.length} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.STATUS)} labelPlacement="integrated">
						<CheckPicker
							data={orderStatuses}
							placeholder={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.STATUS)}
							className={clsx('fc-selector picker-selector status-selector', !status.length && 'fc-added-icon')}
							appearance="subtle"
							placement="bottomStart"
							ref={picker}
							searchable={false}
							onClean={(): void => {
								savedOrderFilters(dispatch, { status: [] }, orderStatuses);
								setIsRefreshed(false);
							}}
							onChange={(value): void => {
								savedOrderFilters(dispatch, { status: value }, orderStatuses);
								setIsRefreshed(false);
							}}
							value={status}
							renderExtraFooter={(): ReactNode => (
								<RenderExtraFooter
									statusFilter={status}
									allValue={allValue}
									handleCheckAll={handleCheckAll}
								/>
							)}
						/>
					</FilterComponent>
					{
						(appliedFilters?.pid || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.PARTNER_ID)) && checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
							<FilterComponent value={pid} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.PARTNER_ID)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={partnerIdRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.PARTNER_ID)}
									close={(): void => partnerIdRef.current.close()}
									open={(): void => partnerIdRef.current.open()}
									value={pid}
									setValue={(value): void => savedOrderFilters(dispatch, { pid: value }, orderStatuses)}
									type="text"
									setIsRefreshed={setIsRefreshed}
									isGuid
								/>
							</FilterComponent>
						)
					}
					{
						(appliedFilters?.sid || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.SITE_ID)) && (
							<FilterComponent value={sid} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.SITE_ID)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={siteIdRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.SITE_ID)}
									close={(): void => siteIdRef.current.close()}
									open={(): void => siteIdRef.current.open()}
									value={sid}
									setValue={(value): void => savedOrderFilters(dispatch, { sid: value })}
									type="text"
									setIsRefreshed={setIsRefreshed}
									isGuid
								/>
							</FilterComponent>
						)
					}
					{
						(appliedFilters?.orderId || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.ORDER_ID)) && (
							<FilterComponent value={orderId} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.ORDER_ID)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={orderIdRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.ORDER_ID)}
									close={(): void => orderIdRef.current.close()}
									open={(): void => orderIdRef.current.open()}
									value={orderId}
									setValue={(value): void => savedOrderFilters(dispatch, { orderId: value })}
									type="text"
									setIsRefreshed={setIsRefreshed}
									isGuid
								/>
							</FilterComponent>
						)
					}
					{
						(appliedFilters?.externalOrderId || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.EXTERNAL_ORDER_ID)) && (
							<FilterComponent value={externalOrderId} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.EXTERNAL_ORDER_ID)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={externalOrderIdRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.EXTERNAL_ORDER_ID)}
									close={(): void => externalOrderIdRef.current.close()}
									open={(): void => externalOrderIdRef.current.open()}
									value={externalOrderId}
									setValue={(value): void => savedOrderFilters(dispatch, { externalOrderId: value })}
									type="text"
									setIsRefreshed={setIsRefreshed}
								/>
							</FilterComponent>
						)
					}
					{
						((!!appliedFilters?.placedFrom && !!appliedFilters?.placedTo) || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.PLACED_DATE)) && (
							<FilterComponent value={placedFrom} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.PLACED_DATE)} labelPlacement="integrated">
								<div className={clsx('fc-datepicker fc-range-datepicker', placedFrom && 'fc-added-icon')}>
									<DateRangePicker
										placeholder={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.PLACED_DATE)}
										placement="auto"
										defaultCalendarValue={[new Date(showStartDate), new Date(showEndDate)]}
										onChange={(value): void => {
											savedOrderFilters(dispatch, { placedFrom: value?.[0], placedTo: value?.[1] });
											setIsRefreshed(false);
										}}
										onClean={(): void => {
											savedOrderFilters(dispatch, { placedFrom: '', placedTo: '' });
											setIsRefreshed(false);
											console.log('cleaned');
										}}
										shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
										value={placedFrom && placedTo ? [new Date(placedFrom), new Date(placedTo)] : null}
										format="yyyy-MM-dd HH:mm:ss"
									/>
								</div>
							</FilterComponent>
						)
					}
					{
						((!!appliedFilters?.from && !!appliedFilters?.to) || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.CREATED_DATE)) && (
							<FilterComponent value={from} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.CREATED_DATE)} labelPlacement="integrated">
								<div className={clsx('fc-datepicker fc-range-datepicker', from && 'fc-added-icon')}>
									<DateRangePicker
										placeholder={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.CREATED_DATE)}
										placement="auto"
										defaultCalendarValue={[new Date(showStartDate), new Date(showEndDate)]}
										onChange={(value): void => {
											savedOrderFilters(dispatch, { from: value?.[0], to: value?.[1] });
											setIsRefreshed(false);
										}}
										onClean={(): void => {
											savedOrderFilters(dispatch, { from: '', to: '' });
											setIsRefreshed(false);
										}}
										shouldDisableDate={(date): boolean => isAfter(date, endOfDay(new Date()))}
										value={from && to ? [new Date(from), new Date(to)] : null}
										format="yyyy-MM-dd HH:mm:ss"
									/>
								</div>
							</FilterComponent>
						)
					}

					{
						(appliedFilters?.firstName || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.FIRST_NAME)) && (
							<FilterComponent value={firstName} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.FIRST_NAME)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={firstNameRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.FIRST_NAME)}
									close={(): void => firstNameRef.current.close()}
									open={(): void => firstNameRef.current.open()}
									value={firstName}
									setValue={(value): void => savedOrderFilters(dispatch, { firstName: value })}
									type="text"
									setIsRefreshed={setIsRefreshed}
								/>
							</FilterComponent>
						)
					}

					{
						(appliedFilters?.lastName || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.LAST_NAME)) && (
							<FilterComponent value={lastName} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.LAST_NAME)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={lastNameRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.LAST_NAME)}
									close={(): void => lastNameRef.current.close()}
									open={(): void => lastNameRef.current.open()}
									value={lastName}
									setValue={(value): void => savedOrderFilters(dispatch, { lastName: value })}
									type="text"
									setIsRefreshed={setIsRefreshed}
								/>
							</FilterComponent>
						)
					}
					{
						(appliedFilters?.email || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.EMAIL)) && (
							<FilterComponent value={email} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.EMAIL)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={emailRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.EMAIL)}
									close={(): void => emailRef.current.close()}
									open={(): void => emailRef.current.open()}
									value={email}
									setValue={(value): void => savedOrderFilters(dispatch, { email: value })}
									type="text"
									setIsRefreshed={setIsRefreshed}
									isEmail
								/>
							</FilterComponent>
						)
					}
					{
						(appliedFilters?.isMIT || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.TYPE)) && (
							<FilterComponent value={isMIT} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.TYPE)} labelPlacement="integrated">
								<SelectPicker
									data={typesList}
									appearance="subtle"
									placement="bottomEnd"
									placeholder={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.TYPE)}
									value={isMIT}
									onSelect={(value): void => {
										savedOrderFilters(dispatch, { isMIT: value });
										setIsRefreshed(false);
									}}
									onClean={(): void => {
										savedOrderFilters(dispatch, { isMIT: '' });
										setIsRefreshed(false);
									}}
									className={clsx('fc-selector timezone-selector', isMIT ? 'fc-selector-cleanable' : 'fc-added-icon')}
									searchable={false}
									defaultValue={isMIT}
								/>
							</FilterComponent>
						)
					}
					{
						(appliedFilters?.bin || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.BIN)) && (
							<FilterComponent value={bin} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.BIN)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={binRef}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.BIN)}
									close={(): void => binRef.current.close()}
									open={(): void => binRef.current.open()}
									value={bin}
									setValue={(value): void => savedOrderFilters(dispatch, { bin: value })}
									type="number"
									isBin
									setIsRefreshed={setIsRefreshed}
								/>
							</FilterComponent>
						)
					}
					{
						(appliedFilters?.last4 || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.LAST_4)) && (
							<FilterComponent value={last4} label={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.LAST_4)} labelPlacement="integrated">
								<NumberFilterComponent
									reference={last4Ref}
									fieldLabel={t(ORDERS_FILTERS_FORM_FIELDS_NAMES.LAST_4)}
									close={(): void => last4Ref.current.close()}
									open={(): void => last4Ref.current.open()}
									value={last4}
									setValue={(value): void => savedOrderFilters(dispatch, { last4: value })}
									type="number"
									isLast4
									setIsRefreshed={setIsRefreshed}
								/>
							</FilterComponent>
						)
					}
					{
						((appliedFilters?.timezone && appliedFilters?.timezone !== 'UTC') || savedOrderFavoriteFilters?.includes(ORDERS_FILTERS_FORM_FIELDS.TIMEZONE)) && (
							<FilterComponent value={timezone} label="Time Zone" labelPlacement="integrated">
								<SelectPicker
									data={timeZonesList}
									appearance="subtle"
									placement="bottomEnd"
									placeholder='Time Zone'
									value={timezone}
									onSelect={(value): void => {
										savedOrderFilters(dispatch, { timezone: value });
										setIsRefreshed(false);
									}}
									className="fc-selector timezone-selector"
									cleanable={false}
									defaultValue={merchantData?.timezoneName ?? 'UTC'}
								/>
							</FilterComponent>
						)
					}
					<ButtonComponent
						onButtonClick={(): void => setIsFilterModalOpen(true)}
						additionalClass="me-5 p-0 pb-2 order-filter-button"
						buttonType={BUTTON_TYPE.FAKE_LINK}
					>
						<img
							alt="filter"
							src={toAbsoluteUrl('/media/icons/filter.svg')}
							className="fc-filter-icon"
						/>
					</ButtonComponent>
					{
						isSelectedFilters && (
							<div
								className="text-danger text-decoration-underline cursor-pointer"
								onClick={handleCleanFilter}
							>
								Clear filters
							</div>
						)
					}

				</div>
			</div>

			<ModalWindow
				{...modalState}
				onClose={onCloseModal}
			/>
			<ModalWindow
				isOpen={isFilterModalOpen}
				modalTitle="Orders filters"
				isRightSideModal={true}
				backdrop="static"
				contentClassName="d-block h-100"
				onClose={handleFilterModalClose}
				onCancel={handleFilterModalClose}
				onConfirm={formik.handleSubmit}
				confirmButtonText="Search"
				additionalConfirmButtonClass="w-70px w-md-100px"
				isConfirmLoading={isOrderDataLoading}
				content={<OrdersFiltersSideModal
					allStatuses={allValue}
					formik={formik}
				/>
				}
			/>

		</div>
	);
};
